/**
 * Locally cached floor-plan licences.
 *
 * IMPORTANT: this is a cache of what the server already granted, not the source
 * of truth. Anyone can write to localStorage, so the backend must re-verify
 * entitlement on every download request. The UI unlock here is convenience —
 * the file delivery is what actually has to be protected.
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import type { Licence, LicenceTierId } from "@/types";

interface LicenceState {
  licences: Record<string, Licence>;
  grant: (licence: Licence) => void;
  revokeAll: () => void;
  isOwned: (planId: string) => boolean;
  licenceFor: (planId: string) => Licence | undefined;
  ownedIds: () => string[];
}

export const useLicenceStore = create<LicenceState>()(
  persist(
    (set, get) => ({
      licences: {},

      grant: (licence) =>
        set((state) => ({
          licences: { ...state.licences, [licence.planId]: licence },
        })),

      revokeAll: () => set({ licences: {} }),

      isOwned: (planId) => Boolean(get().licences[planId]),

      licenceFor: (planId) => get().licences[planId],

      ownedIds: () => Object.keys(get().licences),
    }),
    {
      name: "ind.licences.v1",
      storage: createJSONStorage(() => localStorage),
      version: 1,
    },
  ),
);

/** Selector helpers keep components from re-rendering on unrelated changes. */
export const selectIsOwned = (planId: string) => (state: LicenceState) =>
  Boolean(state.licences[planId]);

export const selectLicence = (planId: string) => (state: LicenceState) => state.licences[planId];

export function tierLabel(tier: LicenceTierId): string {
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}
